<template>
  <div class="px-6 py-6 space-y-6 transition-colors duration-200 min-h-screen text-left"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">
    
    <!-- En-tête -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-5 gap-4"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-1">
        <h1 class="text-xl font-semibold tracking-tight" :class="isDark ? 'text-white' : 'text-zinc-900'">Planning Horaires & Congés</h1>
        <p class="text-xs font-medium">Configurez les grilles de présence et arbitrez les flux de congés de vos opérateurs.</p>
      </div>
      
      <button @click="openAbsenceModal" 
        class="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-lg active:scale-[0.98]"
        :class="isDark ? 'bg-white text-black hover:bg-zinc-200 shadow-white/5' : 'bg-zinc-900 text-white hover:bg-zinc-800'">
        + Poser une Absence / Congé
      </button>
    </div>

    <div class="grid lg:grid-cols-3 gap-6 items-start w-full">
      
      <!-- Matrice des Horaires -->
      <div class="lg:col-span-2 space-y-6">
        <div class="border rounded-2xl p-5 space-y-5 transition-colors" :class="isDark ? 'bg-zinc-900/40 border-zinc-900' : 'bg-white border-zinc-200'">
          <div class="border-b pb-3 flex justify-between items-center" :class="isDark ? 'border-zinc-800' : 'border-zinc-100'">
            <h2 class="text-[10px] font-bold uppercase tracking-widest">Matrice des Horaires Hebdomadaires</h2>
            <select v-model="selectedEmployeeId" class="px-3 py-1.5 rounded-lg border text-[11px] font-semibold focus:outline-none cursor-pointer"
              :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200'">
              <option v-for="emp in staff" :key="emp.id" :value="emp.id">{{ emp.nom }}</option>
            </select>
          </div>

          <div class="space-y-3">
            <div v-for="(shift, day) in currentEmployeeSchedule.shifts" :key="day" 
              class="grid grid-cols-1 sm:grid-cols-4 items-center gap-3 p-3 border rounded-xl transition-all"
              :class="isDark ? 'bg-zinc-950/40 border-zinc-800' : 'bg-zinc-50/50 border-zinc-100'">
              
              <div class="text-[11px] font-bold uppercase tracking-wide opacity-80">{{ day }}</div>
              
              <div class="flex items-center">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" v-model="shift.ouvert" class="sr-only peer" />
                  <div class="w-8 h-4 rounded-full transition-colors" 
                    :class="shift.ouvert ? (isDark ? 'bg-emerald-900' : 'bg-emerald-200') : 'bg-zinc-300'"></div>
                  <span class="ml-2 text-[10px] font-bold uppercase" :class="shift.ouvert ? 'text-emerald-600' : 'text-zinc-400'">
                    {{ shift.ouvert ? 'Planifié' : 'Repos' }}
                  </span>
                </label>
              </div>

              <div v-if="shift.ouvert" class="sm:col-span-2 flex items-center gap-2 text-xs">
                <input type="time" v-model="shift.debut" class="border rounded px-2 py-1 text-center font-mono focus:outline-none" 
                  :class="isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'" />
                <span class="opacity-30">—</span>
                <input type="time" v-model="shift.fin" class="border rounded px-2 py-1 text-center font-mono focus:outline-none" 
                  :class="isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'" />
              </div>
              <div v-else class="sm:col-span-2 text-[10px] opacity-50 italic">Aucun créneau actif</div>
            </div>
          </div>

          <div class="pt-2 flex justify-end">
            <button @click="saveEmployeeSchedule" class="px-5 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-colors border"
              :class="isDark ? 'bg-zinc-800 border-zinc-700 hover:bg-zinc-700' : 'bg-zinc-100 border-zinc-200 hover:bg-zinc-200'">
              Enregistrer les cycles
            </button>
          </div>
        </div>
      </div>

      <!-- Registre des Congés -->
      <div class="w-full">
        <div class="border rounded-2xl p-5 space-y-4 shadow-xl transition-colors" :class="isDark ? 'bg-zinc-900/30 border-zinc-900' : 'bg-white border-zinc-200'">
          <div class="border-b pb-3" :class="isDark ? 'border-zinc-800' : 'border-zinc-100'">
            <h2 class="text-[10px] font-bold uppercase tracking-widest">Registre des Congés</h2>
          </div>

          <div class="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            <div v-for="leave in absences" :key="leave.id" class="border rounded-xl p-4 space-y-3 transition-colors"
              :class="isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-zinc-50 border-zinc-200'">
              
              <div class="flex justify-between items-start">
                <div class="space-y-0.5">
                  <p class="text-xs font-bold">{{ leave.employeNom }}</p>
                  <p class="text-[9px] font-bold uppercase tracking-wider opacity-60">{{ leave.motif }}</p>
                </div>
                <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider border"
                  :class="leave.statut === 'Approuvé' ? 'bg-emerald-950/20 border-emerald-500/30 text-emerald-500' : 'bg-amber-950/20 border-amber-500/30 text-amber-500'">
                  {{ leave.statut }}
                </span>
              </div>

              <p class="text-[10px] font-mono opacity-70">Du {{ formatDate(leave.dateDebut) }} au {{ formatDate(leave.dateFin) }}</p>

              <div v-if="leave.statut === 'En attente'" class="flex gap-2 pt-2">
                <button @click="arbitrateLeave(leave.id, 'Approuvé')" class="flex-1 py-1.5 rounded-lg text-[9px] font-bold uppercase border transition-colors border-emerald-500/30 hover:bg-emerald-500/10">Approuver</button>
                <button @click="arbitrateLeave(leave.id, 'Refusé')" class="flex-1 py-1.5 rounded-lg text-[9px] font-bold uppercase border transition-colors border-red-500/30 hover:bg-red-500/10">Refuser</button>
              </div>
            </div>

            <div v-if="absences.length === 0" class="text-center py-8 opacity-40 text-xs italic">
              Aucune demande en attente.
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();

// Staff from backend
const staff = ref([])

// Target active employee context state
const selectedEmployeeId = ref(null)

// Schedules from backend
const schedules = ref({})

// Leave requests from backend
const absences = ref([])

// Fetch staff from backend
const fetchStaff = async () => {
  try {
    const response = await api.staff.getAllStaff()
    const staffList = response.data?.administrateurs || []

    staff.value = staffList.map(s => ({
      id: s._id,
      nom: s.nom
    }))

    if (staff.value.length > 0 && !selectedEmployeeId.value) {
      selectedEmployeeId.value = staff.value[0].id
    }
  } catch (error) {
    console.error('Erreur lors du chargement du personnel', error)
    toast.error('Erreur lors du chargement du personnel')
  }
}

// Fetch schedules from backend
const fetchSchedules = async () => {
  try {
    for (const emp of staff.value) {
      try {
        const response = await api.staff.getStaffSchedule(emp.id)
        schedules.value[emp.id] = response.data || {
          shifts: {
            lundi: { ouvert: true, debut: "09:00", fin: "18:00" },
            mardi: { ouvert: true, debut: "09:00", fin: "18:00" },
            mercredi: { ouvert: true, debut: "09:00", fin: "18:00" },
            jeudi: { ouvert: true, debut: "09:00", fin: "18:00" },
            vendredi: { ouvert: true, debut: "09:00", fin: "18:00" },
            samedi: { ouvert: true, debut: "09:00", fin: "18:00" },
            dimanche: { ouvert: false, debut: "09:00", fin: "18:00" }
          }
        }
      } catch (error) {
        // If no schedule exists, create default
        schedules.value[emp.id] = {
          shifts: {
            lundi: { ouvert: true, debut: "09:00", fin: "18:00" },
            mardi: { ouvert: true, debut: "09:00", fin: "18:00" },
            mercredi: { ouvert: true, debut: "09:00", fin: "18:00" },
            jeudi: { ouvert: true, debut: "09:00", fin: "18:00" },
            vendredi: { ouvert: true, debut: "09:00", fin: "18:00" },
            samedi: { ouvert: true, debut: "09:00", fin: "18:00" },
            dimanche: { ouvert: false, debut: "09:00", fin: "18:00" }
          }
        }
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement des horaires', error)
  }
}

// Fetch leave requests (absences) from backend
const fetchAbsences = async () => {
  try {
    // TODO: Create proper API endpoint for absences
    // For now, set empty array
    absences.value = []
  } catch (error) {
    console.error('Erreur lors du chargement des congés', error)
  }
}

// Load all data on mount
onMounted(async () => {
  await fetchStaff()
  await fetchSchedules()
  await fetchAbsences()
})

// Target contextual computation layer
const currentEmployeeSchedule = computed(() => {
  if (!selectedEmployeeId.value) {
    return {
      shifts: {
        lundi: { ouvert: true, debut: "09:00", fin: "18:00" },
        mardi: { ouvert: true, debut: "09:00", fin: "18:00" },
        mercredi: { ouvert: true, debut: "09:00", fin: "18:00" },
        jeudi: { ouvert: true, debut: "09:00", fin: "18:00" },
        vendredi: { ouvert: true, debut: "09:00", fin: "18:00" },
        samedi: { ouvert: true, debut: "09:00", fin: "18:00" },
        dimanche: { ouvert: false, debut: "09:00", fin: "18:00" }
      }
    }
  }

  return schedules.value[selectedEmployeeId.value] || {
    shifts: {
      lundi: { ouvert: true, debut: "09:00", fin: "18:00" },
      mardi: { ouvert: true, debut: "09:00", fin: "18:00" },
      mercredi: { ouvert: true, debut: "09:00", fin: "18:00" },
      jeudi: { ouvert: true, debut: "09:00", fin: "18:00" },
      vendredi: { ouvert: true, debut: "09:00", fin: "18:00" },
      samedi: { ouvert: true, debut: "09:00", fin: "18:00" },
      dimanche: { ouvert: false, debut: "09:00", fin: "18:00" }
    }
  }
})

// Methods controls dispatch pipelines
const saveEmployeeSchedule = async () => {
  if (!selectedEmployeeId.value) {
    toast.error('Aucun employé sélectionné')
    return
  }

  try {
    await api.staff.updateStaff(selectedEmployeeId.value, {
      horaires: currentEmployeeSchedule.value.shifts
    })
    toast.success('Horaires sauvegardés', { position: 'top-right' })
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des horaires', error)
    toast.error('Erreur lors de la sauvegarde des horaires')
  }
}

const arbitrateLeave = async (id, newStatus) => {
  try {
    // TODO: Implement proper absence/leave API endpoint
    const target = absences.value.find(a => a.id === id)
    if (target) {
      target.statut = newStatus
      toast.success(`Demande ${newStatus.toLowerCase()}`, { position: 'top-right' })
    }
  } catch (error) {
    toast.error('Erreur lors du traitement de la demande')
  }
}

const openAbsenceModal = () => {
  toast.info('Fonctionnalité à venir', { position: 'top-right' })
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}
</script>

<style scoped>
/* Technical system layout input time overrides controls */
input[type="time"]::-webkit-calendar-picker-indicator {
  filter: invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%);
  cursor: pointer;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>